import { FRIST_KATEGORIEN } from './betreuungTypes';
import { faelligkeitsStufe, heuteIso, tageBis } from './teilnahmeLogic';
import type { FaelligkeitsStufe } from './types';
import {
  KundenportalDaten,
  KundenportalFehlerArt,
  KundenportalStatus,
  PIN_LAENGE,
  PortalBetrieb,
  PortalDokument,
  PortalFrist,
  PortalKontakt,
  PortalMitarbeiter,
  PortalTeilnahme,
  PortalUnterweisung,
  SifaKundenzugang,
} from './kundenportalTypes';

/**
 * Die Regeln des Kundenbereichs — rein, ohne DOM, ohne Supabase:
 * PIN erzeugen und pruefen, Links bauen, Ampeln, Mapper jsonb → Typen,
 * Zustand eines Zugangs fuer die Innendienst-Karte. Alles hier ist in
 * kundenportalLogic.test.ts abgedeckt.
 *
 * DATUMSRECHNUNG PER ZEICHENKETTE wie in teilnahmeLogic: `tageBis` zerlegt
 * `YYYY-MM-DD` selbst; kein `new Date` auf ein reines Tagesdatum.
 */

// ─── PIN ────────────────────────────────────────────────────────────────────

export const istPinForm = (pin: string | null | undefined): boolean =>
  new RegExp(`^[0-9]{${PIN_LAENGE}}$`).test((pin || '').trim());

/** Ganzzahl in [0, n) aus crypto.getRandomValues; Math.random nur als Notnagel. */
export const kryptoZufall = (n: number): number => {
  const c = typeof globalThis !== 'undefined' ? (globalThis as { crypto?: Crypto }).crypto : undefined;
  if (c && typeof c.getRandomValues === 'function') {
    const buf = new Uint32Array(1);
    // Verwerfen statt modulo, sonst waeren kleine Ziffern minimal haeufiger.
    const grenze = Math.floor(0x100000000 / n) * n;
    for (let i = 0; i < 16; i++) {
      c.getRandomValues(buf);
      if (buf[0] < grenze) return buf[0] % n;
    }
    return buf[0] % n;
  }
  return Math.floor(Math.random() * n);
};

/**
 * Neue PIN aus sechs Ziffern. `zufall(n)` liefert eine Ganzzahl in [0, n) —
 * einspeisbar, damit der Test deterministisch ist. Triviale Folgen
 * (alle gleich, auf- oder absteigend) werden verworfen und neu gezogen.
 */
export const erzeugePin = (zufall: (n: number) => number = kryptoZufall): string => {
  for (let versuch = 0; versuch < 50; versuch++) {
    let pin = '';
    for (let i = 0; i < PIN_LAENGE; i++) pin += String(Math.min(9, Math.max(0, Math.floor(zufall(10)))));
    if (!istTrivialePin(pin)) return pin;
  }
  // 50 Ziehungen nur triviale Folgen: praktisch unmoeglich, aber nie eine leere PIN.
  return '204817';
};

/** 000000, 111111, 123456, 654321 … — leicht zu raten, wird nicht vergeben. */
export const istTrivialePin = (pin: string): boolean => {
  if (!istPinForm(pin)) return true;
  const z = pin.split('').map(Number);
  const gleich = z.every((d) => d === z[0]);
  const auf = z.every((d, i) => i === 0 || d === z[i - 1] + 1);
  const ab = z.every((d, i) => i === 0 || d === z[i - 1] - 1);
  return gleich || auf || ab;
};

// ─── Links ──────────────────────────────────────────────────────────────────

/** Der Kundenlink: <origin><pathname>#/kundenportal/<token>. */
export const portalUrl = (origin: string, pathname: string, token: string): string =>
  `${origin}${pathname || '/'}#/kundenportal/${encodeURIComponent(token)}`;

/** Der Teilnehmerlink eines Kurses — derselbe Bau wie unterweisungModulLogic.linkUrl. */
export const unterweisungLinkUrl = (origin: string, pathname: string, linkToken: string): string =>
  `${origin}${pathname || '/'}#/unterweisung/${encodeURIComponent(linkToken)}`;

// ─── Ampeln ─────────────────────────────────────────────────────────────────

/** Fristen: rot ueberfaellig, gelb < 60 Tage, gruen. */
export type FristAmpel = 'ueberfaellig' | 'bald' | 'ok';

export const FRIST_WARN_TAGE = 60;

export const fristAmpel = (faelligAm: string | null | undefined, heute: string = heuteIso()): FristAmpel => {
  const tage = tageBis(faelligAm, heute);
  if (tage === null) return 'ok';
  if (tage < 0) return 'ueberfaellig';
  if (tage < FRIST_WARN_TAGE) return 'bald';
  return 'ok';
};

export const AMPEL_FRIST: Record<FristAmpel, { label: string; klasse: string; punkt: string }> = {
  ueberfaellig: { label: 'überfällig', klasse: 'text-red-700 bg-red-50 border-red-200', punkt: 'bg-red-500' },
  bald: { label: 'bald fällig', klasse: 'text-amber-700 bg-amber-50 border-amber-200', punkt: 'bg-amber-500' },
  ok: { label: 'im Plan', klasse: 'text-emerald-700 bg-emerald-50 border-emerald-200', punkt: 'bg-emerald-500' },
};

export const AMPEL_FRIST_RANG: Record<FristAmpel, number> = { ueberfaellig: 0, bald: 1, ok: 2 };

/** Nachweise je Person: dieselbe Stufe wie im Innendienst (teilnahmeLogic). */
export const teilnahmeAmpel = (gueltigBis: string | null | undefined, heute: string = heuteIso()): FaelligkeitsStufe =>
  faelligkeitsStufe(gueltigBis, heute);

export const AMPEL_NACHWEIS: Record<FaelligkeitsStufe, { label: string; klasse: string; punkt: string }> = {
  gueltig: { label: 'gültig', klasse: 'text-emerald-700 bg-emerald-50 border-emerald-200', punkt: 'bg-emerald-500' },
  bald: { label: 'läuft bald ab', klasse: 'text-amber-700 bg-amber-50 border-amber-200', punkt: 'bg-amber-500' },
  abgelaufen: { label: 'abgelaufen', klasse: 'text-red-700 bg-red-50 border-red-200', punkt: 'bg-red-500' },
  ohne: { label: 'ohne Nachweis', klasse: 'text-slate-500 bg-slate-50 border-slate-200', punkt: 'bg-slate-300' },
};

export const AMPEL_NACHWEIS_RANG: Record<FaelligkeitsStufe, number> = { abgelaufen: 0, bald: 1, ohne: 2, gueltig: 3 };

/** Sortierung offener Fristen: rot zuerst, innerhalb der Stufe nach Datum. */
export const sortiereFristen = (fristen: PortalFrist[], heute: string = heuteIso()): PortalFrist[] =>
  [...fristen].sort(
    (a, b) =>
      AMPEL_FRIST_RANG[fristAmpel(a.faelligAm, heute)] - AMPEL_FRIST_RANG[fristAmpel(b.faelligAm, heute)] ||
      (a.faelligAm || '').localeCompare(b.faelligAm || ''),
  );

const FRIST_LABEL: Record<string, string> = Object.fromEntries(FRIST_KATEGORIEN.map((k) => [k.id, k.label]));

export const fristKategorieLabel = (kategorie: string): string => FRIST_LABEL[kategorie] || kategorie || 'Sonstiges';

export const DOKUMENT_KATEGORIE_LABEL: Record<string, string> = {
  plaene: 'Pläne',
  vertraege: 'Verträge',
  genehmigungen: 'Genehmigungen',
  gefaehrdungen: 'Gefährdungsbeurteilungen',
  betriebsanweisung: 'Betriebsanweisungen',
  nachweise: 'Nachweise',
  protokolle: 'Protokolle',
  fotos: 'Fotos',
  sonstiges: 'Sonstiges',
};

export const dokumentKategorieLabel = (kategorie: string): string =>
  DOKUMENT_KATEGORIE_LABEL[kategorie] || kategorie || 'Sonstiges';

// ─── Personen und Nachweise ─────────────────────────────────────────────────

export interface PortalPersonenStand {
  /** Juengste BESTANDENE Teilnahme. */
  letzte: PortalTeilnahme | null;
  gueltigBis: string | null;
  /** Alle Teilnahmen der Person, juengste zuerst. */
  historie: PortalTeilnahme[];
}

export const LEERER_PERSONEN_STAND: PortalPersonenStand = { letzte: null, gueltigBis: null, historie: [] };

const zeit = (t: PortalTeilnahme): number => Date.parse(t.abgeschlossenAm || '') || 0;

/** Stand je Mitarbeiter-id in einem Durchlauf (Muster mitarbeiterModulLogic.standJePerson). */
export const standJePerson = (teilnahmen: PortalTeilnahme[]): Record<string, PortalPersonenStand> => {
  const out: Record<string, PortalPersonenStand> = {};
  for (const t of teilnahmen || []) {
    if (!t.mitarbeiterId) continue;
    const s = (out[t.mitarbeiterId] ||= { letzte: null, gueltigBis: null, historie: [] });
    s.historie.push(t);
  }
  for (const s of Object.values(out)) {
    s.historie.sort((a, b) => zeit(b) - zeit(a));
    s.letzte = s.historie.find((t) => t.bestanden && !!t.abgeschlossenAm) || null;
    s.gueltigBis = s.letzte?.gueltigBis || null;
  }
  return out;
};

/** Ampel einer Person: `ohne`, wenn es keine bestandene Teilnahme gibt. */
export const personenAmpel = (stand: PortalPersonenStand, heute: string = heuteIso()): FaelligkeitsStufe =>
  stand.letzte ? teilnahmeAmpel(stand.gueltigBis, heute) : 'ohne';

/** Nachname, Vorname — wie eine Personalliste; Inaktive ans Ende. */
export const sortiereMitarbeiter = (liste: PortalMitarbeiter[]): PortalMitarbeiter[] =>
  [...liste].sort(
    (a, b) =>
      Number(b.aktiv) - Number(a.aktiv) ||
      (a.nachname || '').localeCompare(b.nachname || '', 'de') ||
      (a.vorname || '').localeCompare(b.vorname || '', 'de'),
  );

/** Volltextsuche ueber Name, E-Mail, Abteilung, Personalnummer — Wortteile genuegen. */
export const sucheMitarbeiter = (liste: PortalMitarbeiter[], suche: string): PortalMitarbeiter[] => {
  const s = (suche || '').trim().toLowerCase();
  if (!s) return liste;
  const woerter = s.split(/\s+/);
  return liste.filter((m) => {
    const text = `${m.vorname} ${m.nachname} ${m.email || ''} ${m.abteilung || ''} ${m.personalnummer || ''}`.toLowerCase();
    return woerter.every((w) => text.includes(w));
  });
};

/** Anzeigename einer Teilnahme: Stammsatz vor eingefrorener Eingabe. */
export const teilnahmeName = (t: PortalTeilnahme, mitarbeiter: PortalMitarbeiter[]): string => {
  const m = t.mitarbeiterId ? mitarbeiter.find((x) => x.id === t.mitarbeiterId) : undefined;
  const name = m ? `${m.nachname}, ${m.vorname}` : [t.nachname, t.vorname].filter(Boolean).join(', ');
  return name || 'Unbekannt';
};

// ─── Zugang (Innendienst) ───────────────────────────────────────────────────

export type ZugangStatus = 'fehlt' | 'deaktiviert' | 'abgelaufen' | 'gesperrt' | 'aktiv';

export const zugangStatus = (z: SifaKundenzugang | null | undefined, jetzt: Date = new Date()): ZugangStatus => {
  if (!z) return 'fehlt';
  if (!z.aktiv) return 'deaktiviert';
  if (z.aktivBis && Date.parse(z.aktivBis) < jetzt.getTime()) return 'abgelaufen';
  if (z.gesperrtBis && Date.parse(z.gesperrtBis) > jetzt.getTime()) return 'gesperrt';
  return 'aktiv';
};

export const ZUGANG_STATUS_LABEL: Record<ZugangStatus, { label: string; klasse: string }> = {
  fehlt: { label: 'kein Zugang', klasse: 'text-slate-600 bg-slate-100 border-slate-200' },
  deaktiviert: { label: 'deaktiviert', klasse: 'text-slate-600 bg-slate-100 border-slate-200' },
  abgelaufen: { label: 'abgelaufen', klasse: 'text-amber-700 bg-amber-50 border-amber-200' },
  gesperrt: { label: 'gesperrt (Fehlversuche)', klasse: 'text-red-700 bg-red-50 border-red-200' },
  aktiv: { label: 'aktiv', klasse: 'text-emerald-700 bg-emerald-50 border-emerald-200' },
};

/** „noch 37 Minuten" — aus `gesperrtBis` gegen `jetzt`; null, wenn nicht (mehr) gesperrt. */
export const sperrRestText = (gesperrtBis: string | null | undefined, jetzt: Date = new Date()): string | null => {
  if (!gesperrtBis) return null;
  const rest = Date.parse(gesperrtBis) - jetzt.getTime();
  if (!Number.isFinite(rest) || rest <= 0) return null;
  const min = Math.max(1, Math.ceil(rest / 60_000));
  return min === 1 ? 'noch 1 Minute' : `noch ${min} Minuten`;
};

/** Klartext zu einem Fehlstatus der RPCs — fuer die PIN-Seite. */
export const fehlerText = (s: Pick<KundenportalStatus, 'fehler' | 'versucheRest' | 'gesperrtBis' | 'text'>, jetzt: Date = new Date()): string => {
  switch (s.fehler) {
    case 'pin': {
      const rest = s.versucheRest ?? null;
      if (rest === null) return 'Die PIN ist nicht richtig.';
      if (rest <= 0) return 'Die PIN ist nicht richtig. Der Zugang ist jetzt vorübergehend gesperrt.';
      return rest === 1
        ? 'Die PIN ist nicht richtig. Noch 1 Versuch, dann wird der Zugang für eine Stunde gesperrt.'
        : `Die PIN ist nicht richtig. Noch ${rest} Versuche, dann wird der Zugang für eine Stunde gesperrt.`;
    }
    case 'gesperrt': {
      const rest = sperrRestText(s.gesperrtBis, jetzt);
      return `Zu viele Fehlversuche — der Zugang ist vorübergehend gesperrt${rest ? ` (${rest})` : ''}. Bitte später erneut versuchen oder die Fachkraft für Arbeitssicherheit ansprechen.`;
    }
    case 'unbekannt':
      return 'Dieser Link ist nicht gültig.';
    case 'inaktiv':
      return 'Dieser Zugang ist nicht (mehr) freigeschaltet. Bitte die Fachkraft für Arbeitssicherheit ansprechen.';
    case 'schema':
      return 'Der Kundenbereich ist auf dem Server noch nicht eingerichtet.';
    case 'keine_datei':
      return 'Die Datei ist noch nicht abgelegt. Bitte die Fachkraft für Arbeitssicherheit ansprechen.';
    case 'netz':
      return s.text || 'Der Server ist gerade nicht erreichbar. Bitte die Internetverbindung prüfen.';
    case 'eingabe':
    default:
      return s.text || 'Die Angaben wurden nicht angenommen.';
  }
};

// ─── Mapper jsonb → Typen ───────────────────────────────────────────────────

type Zeile = Record<string, unknown>;

const str = (v: unknown): string => (typeof v === 'string' ? v : v == null ? '' : String(v));
const strOrNull = (v: unknown): string | null => {
  if (v == null) return null;
  const s = str(v);
  return s === '' ? null : s;
};
const num = (v: unknown, fallback: number): number => {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : fallback;
};
const numOrNull = (v: unknown): number | null => (v == null ? null : num(v, 0));
const bool = (v: unknown, fallback: boolean): boolean => (typeof v === 'boolean' ? v : v === 'true' ? true : v === 'false' ? false : fallback);
const arr = (v: unknown): Zeile[] => (Array.isArray(v) ? (v.filter((x) => x && typeof x === 'object') as Zeile[]) : []);
const obj = (v: unknown): Zeile => (v && typeof v === 'object' && !Array.isArray(v) ? (v as Zeile) : {});

/** jsonb-Antwort einer RPC als Objekt; Zeichenketten werden geparst, alles andere ist null. */
export const alsObjekt = (data: unknown): Zeile | null => {
  if (data == null) return null;
  if (typeof data === 'string') {
    try {
      const parsed = JSON.parse(data);
      return parsed && typeof parsed === 'object' ? (parsed as Zeile) : null;
    } catch {
      return null;
    }
  }
  return typeof data === 'object' ? (data as Zeile) : null;
};

const FEHLER_ARTEN: KundenportalFehlerArt[] = ['unbekannt', 'inaktiv', 'pin', 'gesperrt', 'eingabe', 'keine_datei', 'schema', 'netz'];

/** `{ ok: false, fehler, … }` einer RPC → Status; unbekannte Werte werden 'unbekannt'. */
export const mapStatus = (row: Zeile | null): KundenportalStatus => {
  const art = str(row?.fehler) as KundenportalFehlerArt;
  return {
    ok: false,
    fehler: FEHLER_ARTEN.includes(art) ? art : 'unbekannt',
    versucheRest: row?.versucheRest == null ? null : num(row.versucheRest, 0),
    gesperrtBis: strOrNull(row?.gesperrtBis),
    text: strOrNull(row?.text),
  };
};

export const mapPortalMitarbeiter = (m: Zeile): PortalMitarbeiter => ({
  id: str(m.id),
  vorname: str(m.vorname),
  nachname: str(m.nachname),
  geburtsdatum: strOrNull(m.geburtsdatum),
  email: strOrNull(m.email),
  abteilung: strOrNull(m.abteilung),
  personalnummer: strOrNull(m.personalnummer),
  aktiv: bool(m.aktiv, true),
  updatedAt: str(m.updatedAt),
});

const mapUnterweisung = (u: Zeile): PortalUnterweisung => ({
  id: str(u.id),
  nummer: num(u.nummer, 0),
  titel: str(u.titel),
  beschreibung: strOrNull(u.beschreibung),
  status: str(u.status) || 'aktiv',
  gueltigMonate: num(u.gueltigMonate, 12),
  dauerMin: numOrNull(u.dauerMin),
  linkToken: strOrNull(u.linkToken),
});

const mapTeilnahme = (t: Zeile): PortalTeilnahme => ({
  id: str(t.id),
  mitarbeiterId: strOrNull(t.mitarbeiterId),
  vorname: strOrNull(t.vorname),
  nachname: strOrNull(t.nachname),
  unterweisungId: str(t.unterweisungId),
  abgeschlossenAm: strOrNull(t.abgeschlossenAm),
  bestanden: bool(t.bestanden, false),
  gueltigBis: strOrNull(t.gueltigBis),
  zertifikatNr: strOrNull(t.zertifikatNr),
  zertifikatVorhanden: bool(t.zertifikatVorhanden, false),
});

const mapFrist = (f: Zeile): PortalFrist => ({
  id: str(f.id),
  kategorie: str(f.kategorie) || 'sonstiges',
  titel: str(f.titel),
  faelligAm: str(f.faelligAm).slice(0, 10),
  intervallMonate: numOrNull(f.intervallMonate),
  zustaendig: strOrNull(f.zustaendig),
  notiz: strOrNull(f.notiz),
});

const mapDokument = (d: Zeile): PortalDokument => ({
  id: str(d.id),
  titel: str(d.titel) || str(d.dateiname),
  dateiname: str(d.dateiname),
  kategorie: str(d.kategorie) || 'sonstiges',
  ordner: str(d.ordner),
  groesseBytes: num(d.groesseBytes, 0),
  mimeTyp: strOrNull(d.mimeTyp),
  stand: strOrNull(d.stand),
  gueltigBis: strOrNull(d.gueltigBis),
  createdAt: str(d.createdAt),
});

const mapBetrieb = (b: Zeile): PortalBetrieb => ({
  name: str(b.name),
  projektnummer: strOrNull(b.projektnummer),
  adresse: strOrNull(b.adresse),
  kundeName: strOrNull(b.kundeName),
  branche: strOrNull(b.branche),
  berufsgenossenschaft: strOrNull(b.berufsgenossenschaft),
  beschaeftigte: numOrNull(b.beschaeftigte),
  betreuungsgruppe: strOrNull(b.betreuungsgruppe),
  betriebsarzt: strOrNull(b.betriebsarzt),
  sicherheitsbeauftragte: strOrNull(b.sicherheitsbeauftragte),
  ersthelfer: numOrNull(b.ersthelfer),
});

const mapKontakt = (k: Zeile): PortalKontakt => ({
  name: strOrNull(k.name),
  telefon: strOrNull(k.telefon),
  email: strOrNull(k.email),
});

/** Das Ergebnis von `sifa_kundenportal_laden` bei `ok: true`. */
export const mapKundenportalDaten = (row: Zeile): KundenportalDaten => ({
  geladenAm: str(row.geladenAm),
  bueroName: str(row.bueroName),
  betrieb: mapBetrieb(obj(row.betrieb)),
  sifaKontakt: mapKontakt(obj(row.sifaKontakt)),
  mitarbeiter: sortiereMitarbeiter(arr(row.mitarbeiter).map(mapPortalMitarbeiter)),
  unterweisungen: arr(row.unterweisungen).map(mapUnterweisung),
  teilnahmen: arr(row.teilnahmen).map(mapTeilnahme),
  fristen: arr(row.fristen).map(mapFrist),
  dokumente: arr(row.dokumente).map(mapDokument),
});

/** Eine Zeile aus `sifa_kundenzugaenge` (ohne pin_hash) → Innendienst-Typ. */
export const mapKundenzugang = (row: Zeile): SifaKundenzugang => ({
  id: str(row.id),
  projectId: str(row.project_id),
  token: str(row.token),
  aktiv: bool(row.aktiv, true),
  aktivBis: strOrNull(row.aktiv_bis),
  fehlversuche: num(row.fehlversuche, 0),
  gesperrtBis: strOrNull(row.gesperrt_bis),
  letzterZugriff: strOrNull(row.letzter_zugriff),
  companyName: strOrNull(row.company_name),
  createdBy: strOrNull(row.created_by),
  createdAt: str(row.created_at),
  updatedAt: str(row.updated_at),
});

/** Person, wie der Portal-Dialog sie an die RPC gibt — nur die erlaubten Felder, getrimmt. */
export const bereinigeMitarbeiterEingabe = (e: {
  id?: string | null;
  vorname?: string | null;
  nachname?: string | null;
  geburtsdatum?: string | null;
  email?: string | null;
  abteilung?: string | null;
  personalnummer?: string | null;
  aktiv?: boolean;
}): Record<string, unknown> => {
  const t = (v: string | null | undefined): string | null => {
    const s = (v ?? '').trim();
    return s ? s : null;
  };
  const out: Record<string, unknown> = {
    vorname: (e.vorname ?? '').trim(),
    nachname: (e.nachname ?? '').trim(),
    geburtsdatum: t(e.geburtsdatum),
    email: t(e.email),
    abteilung: t(e.abteilung),
    personalnummer: t(e.personalnummer),
    aktiv: e.aktiv !== false,
  };
  const id = (e.id ?? '').trim();
  if (id) out.id = id;
  return out;
};

/** Pflichtfelder und Formen VOR dem Aufruf — dieselben Regeln wie in der RPC, nur frueher. */
export const pruefeMitarbeiterEingabe = (e: { vorname?: string | null; nachname?: string | null; email?: string | null; geburtsdatum?: string | null }): string | null => {
  if (!(e.vorname ?? '').trim() || !(e.nachname ?? '').trim()) return 'Vorname und Nachname sind Pflicht.';
  const mail = (e.email ?? '').trim();
  if (mail && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(mail)) return 'Die E-Mail-Adresse ist nicht gültig.';
  const gb = (e.geburtsdatum ?? '').trim();
  if (gb && !/^\d{4}-\d{2}-\d{2}$/.test(gb)) return 'Das Geburtsdatum muss die Form JJJJ-MM-TT haben.';
  return null;
};

// ─── Anzeige ────────────────────────────────────────────────────────────────

/** „2026-09-07" → „07.09.2026" per Zeichenkette — kein new Date auf ein reines Datum. */
export const formatDatum = (iso: string | null | undefined): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec((iso || '').trim());
  return m ? `${m[3]}.${m[2]}.${m[1]}` : iso ? iso : '—';
};

/** Zeitstempel als TT.MM.JJJJ, HH:MM (Ortszeit). */
export const formatZeit = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()}, ${p(d.getHours())}:${p(d.getMinutes())}`;
};

export const formatBytes = (bytes: number | null | undefined): string => {
  const b = Math.max(0, Number(bytes) || 0);
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`;
};

/** „ohne Ablauf" / „12 Monate". */
export const gueltigkeitText = (monate: number | null | undefined): string =>
  !monate || monate <= 0 ? 'ohne Ablauf' : monate === 1 ? '1 Monat' : `${monate} Monate`;

